import type { SlotSignageContent } from "@workspace/api-client-react";
import { t } from "@workspace/strings/web";
import { ZoneWeb } from "@/player/components/zone-web";
import { ClockWidget } from "@/player/components/widgets/clock";
import { WeatherWidget } from "@/player/components/widgets/weather";
import { TickerWidget } from "@/player/components/widgets/ticker";
import { QrWidget } from "@/player/components/widgets/qr";
import { CountdownWidget } from "@/player/components/widgets/countdown";
import {
  NowPlayingWidget,
  type ZoneNowPlaying,
} from "@/player/components/widgets/now-playing";
import { NewsWidget } from "@/player/components/widgets/news";
import { MenuWidget } from "@/player/components/widgets/menu";

function ZoneWidget({
  content,
  nowPlaying,
}: {
  content: SlotSignageContent;
  nowPlaying: ZoneNowPlaying | null;
}) {
  const config = (content.config as Record<string, unknown> | null) ?? null;
  switch (content.widgetKind) {
    case "clock":
      return <ClockWidget config={config} />;
    case "weather":
      return <WeatherWidget config={config} />;
    case "ticker":
      return <TickerWidget config={config} title={content.title} />;
    case "qr":
      return <QrWidget config={config} />;
    case "countdown":
      return <CountdownWidget config={config} />;
    case "nowplaying":
      return <NowPlayingWidget config={config} nowPlaying={nowPlaying} />;
    case "news":
      return <NewsWidget signageContentId={content.id} config={config} />;
    case "menu":
      return <MenuWidget config={config} />;
    default:
      return (
        <div className="flex h-full w-full items-center justify-center bg-background text-muted-foreground">
          {t("player.widgets.unknown")}
        </div>
      );
  }
}

/**
 * Fullscreen signage zone for non-file content on the video channel: a web
 * page tile (sandboxed iframe) or a built-in widget. Rendered by the player
 * shells INSTEAD of SignageCrossfade when the resolved video slot carries
 * signageContent — hard-cut in/out, no crossfade for live pages/widgets.
 */
export function ZoneSignage({
  content,
  className,
  nowPlaying = null,
}: {
  content: SlotSignageContent;
  className: string;
  /** The player's own on-air audio, for the now-playing widget. */
  nowPlaying?: ZoneNowPlaying | null;
}) {
  if (content.kind === "web") {
    return <ZoneWeb content={content} className={className} />;
  }
  return (
    <div className={className} data-testid="zone-widget">
      <ZoneWidget content={content} nowPlaying={nowPlaying} />
    </div>
  );
}
